import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ErrorMessage, Field, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import CustomInput from "../../../CustomControls/CustomInput";
import CustomSelect from "../../../CustomControls/CustomSelect";
import CustomFlatpickr from "../../../CustomControls/CustomFlatpickr";
import CustomSelectAsync from "../../../CustomControls/CustomSelectAsync";
import moment from "moment";
import { Spin } from "antd";
import { get, post } from "../../../helpers/api_helper";
import {
  POST_GetBranchBankDDLList,
  POST_GetCustomerDDLListAJAX,
  POST_GetPaymentModeList,
  POST_GetPaymentReceiptCustomerDetails,
  POST_GetPaymentReceiptDetailByID,
  POST_PaymentReceiptInsert,
  POST_PaymentReceiptUpdate,
} from "../../../helpers/url_helper";
import CustomTextArea from "../../../CustomControls/CustomTextArea";
import { SVGICON } from "../../../constant/theme";
import {
  errorMessage,
  successMessage,
  warningMessage,
} from "../CommonFunctions";

const AddPaymentReceipt = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { PrimaryIdParam } = useParams();

  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));

  const [modeOfPaymentOptions, setModeOfPaymentOptions] = useState([]);
  const [tdsCalcTypeOptions, setTDSCalcTypeOptions] = useState([]);
  const [receivableBankOptions, setReceivableBankOptions] = useState([]);
  const [financialYearOptions, setFinancialYearOptions] = useState([]);

  const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState({});

  const getFinancialYears = () => {
    let startYear = 2023,
      endYear = new Date().getFullYear() + 1;
    let yearOptions = [];
    for (let index = startYear; index <= endYear; index++) {
      let fYear = index + "-" + (index + 1);
      yearOptions.push({ label: fYear, value: fYear });
    }
    setFinancialYearOptions(yearOptions);
  };

  const getTDSCalcTypeDDL = () => {
    setTDSCalcTypeOptions([
      {
        value: 1,
        label: "Basic Amount",
      },
      {
        value: 2,
        label: "Net Amount",
      },
    ]);
  };

  const getModeOfPaymentDDL = async () => {
    setIsLoading(true);
    await get(POST_GetPaymentModeList)
      .then((response) => {
        if (response?.IsSuccess) {
          setModeOfPaymentOptions(
            response?.List?.map((item) => ({
              label: item.PaymentMode,
              value: item.PaymentModeID,
            }))
          );
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const getBranchBankDDL = async () => {
    setIsLoading(true);
    await post(POST_GetBranchBankDDLList, {
      BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          setReceivableBankOptions(
            response?.List?.map((item) => ({
              label: item.BankName,
              value: item.BranchBankIDEncrypted,
            }))
          );
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        isLoading(false);
      });
  };

  const validationSchema = Yup.object().shape({
    PaymentDate: Yup.string().required("Payment Date is required field"),
    CustomerIDEncrypted: Yup.string().required("Customer is required field"),
  });

  const formik = useFormik({
    validationSchema,
    onSubmit: (values, formikHelpers) =>
      handleValidSubmit(values, formikHelpers),
    initialValues: {
      PaymentReceiptIDEncrypted: PrimaryIdParam
        ? window.atob(PrimaryIdParam)
        : "",
      PaymentReceiptNo: "",
      PaymentDate: new Date(),
      CustomerIDEncrypted: "",
      CustomerName: "",
      Remarks: "",
      IsSettledAgainstAdvance: false,
      ListOfAdvanceReceipt: [],
      ListOfInvoice: [],
      CustomerUnsettledAmount: "0.00",
      TotalAdvanceToBeSettled: "0.00",
      TotalTDSAmount: "0.00",
      TotalKasar: "0.00",
      TotalBankCharges: "0.00",
      TotalReceivableAmount: "0.00",
      IsCancelled: false,
    },
    validateOnBlur: false,
  });

  const {
    values: {
      PaymentDate,
      CustomerIDEncrypted,
      CustomerName,
      IsSettledAgainstAdvance,
      ListOfAdvanceReceipt,
      ListOfInvoice,
      CustomerUnsettledAmount,
      TotalAdvanceToBeSettled,
      TotalTDSAmount,
      TotalKasar,
      TotalBankCharges,
      TotalReceivableAmount,
      IsCancelled,
    },
  } = formik;

  const handleValidSubmit = (values) => {
    if (values?.IsCancelled == true) {
      return;
    }
    if (
      values?.ListOfInvoice?.filter((x) => x.IsSelected == true)?.length == 0
    ) {
      formik.setFieldError(
        "ListOfInvoice",
        "Please select at least one invoice."
      );
      formik.setFieldTouched("ListOfInvoice", true);
      return;
    }
    if (values?.IsSettledAgainstAdvance == true) {
      if (
        values?.ListOfAdvanceReceipt?.filter((x) => x.IsSelected == true)
          ?.length == 0
      ) {
        formik.setFieldError(
          "ListOfAdvanceReceipt",
          "Please select at least one advance receipt."
        );
        formik.setFieldTouched("ListOfAdvanceReceipt", true);
        return;
      } else {
        if (
          parseFloat(values?.TotalAdvanceToBeSettled) <
          parseFloat(TotalReceivableAmount)
        ) {
          warningMessage(
            `"Total Advance to be settled amount" must be greater or equal to "Total Receivable amount".`
          );
          return;
        }
      }
    } else {
      if (
        values?.ListOfInvoice?.filter(
          (x) => x.IsSelected == true && x.ModeOfPayment == 0
        )?.length > 0
      ) {
        formik.setFieldError(
          "ListOfInvoice",
          `Invoice No "${
            values?.ListOfInvoice?.filter(
              (x) => x.IsSelected == true && x.ModeOfPayment == 0
            )[0]?.InvoiceNo
          }" Mode of Payment is required.`
        );
        formik.setFieldTouched("ListOfInvoice", true);
        return;
      }
    }
    if (
      values?.ListOfInvoice?.filter(
        (x) => x.IsSelected == true && x.Amount && parseFloat(x.Amount) < 0
      )?.length > 0
    ) {
      formik.setFieldError(
        "ListOfInvoice",
        `Invoice No "${
          values?.ListOfInvoice?.filter(
            (x) => x.IsSelected == true && x.Amount && parseFloat(x.Amount) < 0
          )[0]?.InvoiceNo
        }" please check Receivable Amount.`
      );
      formik.setFieldTouched("ListOfInvoice", true);
      return;
    } else {
      let isValidAmount = true;
      Promise.all(
        values?.ListOfInvoice?.filter((x) => x.IsSelected == true)?.map(
          (item) => {
            let totalAmount =
              parseFloat(item.TDSAmount ? item.TDSAmount : 0) +
              parseFloat(item.Kasar ? item.Kasar : 0) +
              parseFloat(item.BankCharges ? item.BankCharges : 0) +
              parseFloat(item.Amount ? item.Amount : 0);
            if (parseFloat(item.PendingAmount) < totalAmount) {
              formik.setFieldError(
                "ListOfInvoice",
                `Invoice No "${item.InvoiceNo}" please check all amount it can not be greater then pending amount.`
              );
              formik.setFieldTouched("ListOfInvoice", true);
              isValidAmount = false;
              return;
            }
          }
        )
      );
      if (isValidAmount == false) {
        return;
      }
    }

    if (
      values?.ListOfInvoice?.filter(
        (x) =>
          x.IsSelected == true &&
          x.TDSAmount &&
          parseFloat(x.TDSAmount) > 0 &&
          !x?.FinancialYear
      )?.length > 0
    ) {
      formik.setFieldError(
        "ListOfInvoice",
        `Invoice No "${
          values?.ListOfInvoice?.filter(
            (x) =>
              x.IsSelected == true &&
              x.TDSAmount &&
              parseFloat(x.TDSAmount) > 0 &&
              !x?.FinancialYear
          )[0]?.InvoiceNo
        }" TDS Year is required.`
      );
      formik.setFieldTouched("ListOfInvoice", true);
      return;
    }

    setIsLoading(true);

    let invoiceList = [];

    Promise.all(
      values?.ListOfInvoice?.filter((x) => x.IsSelected == true)?.map(
        (item) => {
          invoiceList.push({
            ...item,
            TDSPercentage: item.TDSPercentage ? item.TDSPercentage : "0.00",
            TDSCalcType: item.TDSCalcType ? item.TDSCalcType : 0,
            TDSAmount: item.TDSAmount ? item.TDSAmount : "0.00",
            Kasar: item.Kasar ? item.Kasar : "0.00",
            BankCharges: item.BankCharges ? item.BankCharges : "0.00",
          });
        }
      )
    );

    let payload = {
      ...values,
      PaymentDate: values?.PaymentDate
        ? moment(values?.PaymentDate).format("YYYY-MM-DD").replace(/^\-/, "")
        : "",
      AdvanceReceiptIDs:
        values?.IsSettledAgainstAdvance == true
          ? Array.from(
              values?.ListOfAdvanceReceipt?.filter(
                (item) => item.IsSelected == true
              ),
              (x) => x.AdvanceReceiptIDEncrypted
            ).join(",")
          : "",
      ListOfInvoiceDetails: invoiceList,
      Amount: values?.TotalReceivableAmount,
      TDSAmount: values?.TotalTDSAmount,
      Kasar: values?.TotalKasar,
      BankCharges: values?.TotalBankCharges,
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
      CreatedByEncrypted: AuthUser?.ReferenceIDEncrypt,
      ModifiedByEncrypted: AuthUser?.ReferenceIDEncrypt,
    };

    delete payload["ListOfAdvanceReceipt"];
    delete payload["ListOfInvoice"];
    delete payload["TotalTDSAmount"];
    delete payload["TotalKasar"];
    delete payload["TotalBankCharges"];
    delete payload["TotalReceivableAmount"];
    // console.log(payload);

    post(
      PrimaryIdParam ? POST_PaymentReceiptUpdate : POST_PaymentReceiptInsert,
      payload
    )
      .then((response) => {
        setIsLoading(true);
        if (response?.IsSuccess) {
          successMessage(response?.Message);
          setTimeout(() => {
            navigate(-1);
          }, 1000);
        } else {
          errorMessage(response?.Message);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!PaymentDate) {
      formik.setFieldValue("PaymentDate", "");
    }
  }, [PaymentDate]);

  useEffect(() => {
    getFinancialYears();
    getTDSCalcTypeDDL();
    getModeOfPaymentDDL();
    getBranchBankDDL();
    if (PrimaryIdParam) {
      getPaymentReceiptDetails();
    }
  }, []);

  const getPaymentReceiptDetails = async () => {
    setIsLoading(true);
    await post(POST_GetPaymentReceiptDetailByID, {
      PaymentReceiptIDEncrypted: window.atob(PrimaryIdParam),
    })
      .then((response) => {
        if (response?.IsSuccess) {
          formik.setFieldValue("PaymentReceiptNo", response?.PaymentReceiptNo);
          formik.setFieldValue(
            "PaymentDate",
            response?.PaymentDate ? new Date(response?.PaymentDate) : ""
          );
          formik.setFieldValue(
            "CustomerIDEncrypted",
            response?.CustomerIDEncrypted
          );
          formik.setFieldValue("CustomerName", response?.CustomerName);
          formik.setFieldValue("Remarks", response?.Remarks);
          formik.setFieldValue(
            "IsSettledAgainstAdvance",
            response?.IsSettledAgainstAdvance
          );
          formik.setFieldValue(
            "ListOfAdvanceReceipt",
            response?.ListOfAdvanceReceipt
          );
          formik.setFieldValue("ListOfInvoice", response?.ListOfInvoice);
          formik.setFieldValue(
            "CustomerUnsettledAmount",
            response?.UnsettledAmount
          );
          formik.setFieldValue("TotalTDSAmount", response?.TDSAmount);
          formik.setFieldValue("TotalKasar", response?.Kasar);
          formik.setFieldValue("TotalBankCharges", response?.BankCharges);
          formik.setFieldValue("TotalReceivableAmount", response?.Amount);
          formik.setFieldValue("IsCancelled", response?.IsCancelled);
          setCalcTotalAdvance(true);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const loadOptionsCutomer = (inputValue) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(getCustomerDDL_AJAX(inputValue));
      }, 1000);
    });
  };

  const getCustomerDDL_AJAX = async (inputValue) => {
    return await post(POST_GetCustomerDDLListAJAX, {
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      SearchValue: inputValue ?? "",
      TPIFlag: -1, // 0 = Only Customer, 1 = Only TPI,  -1 = All (Customer & TPI both)
    })
      .then((response) => {
        if (response?.IsSuccess) {
          return response?.CustomerDDLList?.map((item) => ({
            label: item.CustomerName,
            value: item.CustomerIDEncrypted,
          }));
        } else {
          return [];
        }
      })
      .catch((error) => {
        console.log(error);
        return [];
      });
  };

  const getCustomerDetailsForPaymentReceipt = async (customerID) => {
    formik.setFieldValue("CustomerUnsettledAmount", "0.00");
    formik.setFieldValue("IsSettledAgainstAdvance", false);
    formik.setFieldValue("ListOfAdvanceReceipt", []);
    formik.setFieldValue("ListOfInvoice", []);
    if (customerID) {
      setIsLoading(true);
      await post(POST_GetPaymentReceiptCustomerDetails, {
        BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
        CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
        CustomerIDEncrypted: customerID,
      })
        .then((response) => {
          if (response?.IsSuccess) {
            formik.setFieldValue(
              "CustomerUnsettledAmount",
              response?.UnsettledAmount
            );
            formik.setFieldValue(
              "ListOfAdvanceReceipt",
              response?.ListOfAdvanceReceipt?.map((item) => ({
                ...item,
                IsSelected: false,
              }))
            );
            formik.setFieldValue(
              "ListOfInvoice",
              response?.ListOfInvoice?.map((item) => ({
                ...item,
                IsSelected: false,
                TDSCalcType: "",
                TDSAmount: "",
                Kasar: "",
                BankCharges: "",
                Amount: "",
                ModeOfPayment: 0,
                ReceivableBankIDEncrypted: "",
                PaymentRefNo: "",
                PaymentRefBank: "",
                FinancialType: "",
              }))
            );
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
          isLoading(false);
        });
    }
    setCalcTotalAdvance(true);
    setCalcInvoiceTotal(true);
  };

  const handleChangeCustomer = (e) => {
    formik.setFieldValue("CustomerIDEncrypted", e?.value ?? "");
    formik.setFieldValue("CustomerName", e?.label ?? "");
    if ((e?.value ?? "") !== CustomerIDEncrypted) {
      getCustomerDetailsForPaymentReceipt(e?.value ?? "");
    }
  };

  const handleChangeIsSettledAgainstAdvance = (checked) => {
    formik.setFieldValue("IsSettledAgainstAdvance", checked);
  };

  const handleChangeIsSelectedAdvance = (checked, index) => {
    formik.setFieldValue(`ListOfAdvanceReceipt[${index}].IsSelected`, checked);
    setCalcTotalAdvance(true);
  };

  const handleChangeIsSelectedInvoice = (checked, index) => {
    formik.setFieldValue(`ListOfInvoice[${index}].IsSelected`, checked);
  };

  const handleChangeFinancialYear = (e, index) => {
    formik.setFieldValue(
      `ListOfInvoice[${index}].FinancialYear`,
      e?.value ?? ""
    );
    if (e && e?.value) {
      ListOfInvoice?.map((item, idx) => {
        if (!item.FinancialYear) {
          formik.setFieldValue(
            `ListOfInvoice[${idx}].FinancialYear`,
            e?.value ?? ""
          );
        }
      });
    }
  };

  const [calcTotalAdvance, setCalcTotalAdvance] = useState(false);

  const calculateTotalAdvanceToBeSettled = () => {
    let totalAmount = 0;
    Promise.all(
      ListOfAdvanceReceipt?.filter((x) => x.IsSelected == true)?.map((item) => {
        totalAmount = totalAmount + parseFloat(item.AvailableAmount);
      })
    );
    formik.setFieldValue("TotalAdvanceToBeSettled", totalAmount.toFixed(2));
  };

  useEffect(() => {
    if (calcTotalAdvance == true) {
      calculateTotalAdvanceToBeSettled();
      setCalcTotalAdvance(false);
    }
  }, [calcTotalAdvance]);

  const calculateInvoiceTDSAmount = (index, tdsPercentage, tdsCalcType) => {
    let amount = 0,
      tdsAmount = 0;
    if (tdsCalcType == 1) {
      amount = ListOfInvoice[index].GrossAmount;
    }
    if (tdsCalcType == 2) {
      amount = ListOfInvoice[index].NetAmount;
    }
    tdsAmount =
      (parseFloat(amount) * parseFloat(tdsPercentage ? tdsPercentage : 0)) /
      100;
    formik.setFieldValue(
      `ListOfInvoice[${index}].TDSAmount`,
      tdsAmount.toFixed(2)
    );
    calculateReceivableAmount(
      index,
      ListOfInvoice[index].PendingAmount,
      tdsAmount.toFixed(2),
      ListOfInvoice[index].Kasar,
      ListOfInvoice[index].BankCharges
    );
  };

  const calculateReceivableAmount = (
    index,
    pendingAmount,
    tdsAmount,
    kasar,
    bankCharge
  ) => {
    let amount = 0;
    amount =
      parseFloat(pendingAmount ? pendingAmount : 0) -
      (parseFloat(tdsAmount ? tdsAmount : 0) +
        parseFloat(kasar ? kasar : 0) +
        parseFloat(bankCharge ? bankCharge : 0));
    formik.setFieldValue(`ListOfInvoice[${index}].Amount`, amount.toFixed(2));
    setCalcInvoiceTotal(true);
  };

  const [calcInvoiceTotal, setCalcInvoiceTotal] = useState(false);

  const calculateInvoiceTotal = () => {
    let totalTDSAmount = 0,
      totalKasar = 0,
      totalBankCharges = 0,
      totalAmount = 0;
    Promise.all(
      ListOfInvoice?.filter((x) => x.IsSelected == true)?.map((item) => {
        totalTDSAmount =
          totalTDSAmount + parseFloat(item.TDSAmount ? item.TDSAmount : 0);
        totalKasar = totalKasar + parseFloat(item.Kasar ? item.Kasar : 0);
        totalBankCharges =
          totalBankCharges +
          parseFloat(item.BankCharges ? item.BankCharges : 0);
        totalAmount = totalAmount + parseFloat(item.Amount ? item.Amount : 0);
      })
    );
    formik.setFieldValue("TotalTDSAmount", totalTDSAmount.toFixed(2));
    formik.setFieldValue("TotalKasar", totalKasar.toFixed(2));
    formik.setFieldValue("TotalBankCharges", totalBankCharges.toFixed(2));
    formik.setFieldValue("TotalReceivableAmount", totalAmount.toFixed(2));
  };

  useEffect(() => {
    if (calcInvoiceTotal == true) {
      calculateInvoiceTotal();
      setCalcInvoiceTotal(false);
    }
  }, [calcInvoiceTotal]);

  return (
    <>
      <div className="card m-1">
        <div className="card-body p-1">
          <Spin
            size="large"
            spinning={isLoading}
            tip={"Loading..."}
            style={{ position: "fixed" }}
          >
            <div className="tbl-caption p-2">
              <h4 className="heading mb-0">
                {PrimaryIdParam ? "Edit" : "Add"} Payment Receipt
              </h4>
            </div>
            <hr className="mt-0 mb-1"></hr>
            <div className="p-2 pt-0">
              <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                  <Row>
                    {PrimaryIdParam && (
                      <Col lg={3}>
                        <Field
                          label="Payment Receipt No"
                          name="PaymentReceiptNo"
                          component={CustomInput}
                          required
                          disabled={true}
                        />
                      </Col>
                    )}
                    <Col lg={3}>
                      <Field
                        label="Payment Date"
                        name="PaymentDate"
                        placeholder={"Select Payment Date"}
                        component={CustomFlatpickr}
                        options={{
                          altInput: true,
                          altFormat: "j-M-Y",
                          dateFormat: "d-m-Y",
                          maxDate: new Date(),
                        }}
                        required
                      />
                    </Col>
                    <Col lg={3}>
                      {CustomerIDEncrypted && (
                        <span
                          className="me-2 mt-1 fs-12 text-primary"
                          style={{ float: "right" }}
                        >
                          Unsettled Amount : {CustomerUnsettledAmount}
                        </span>
                      )}

                      {!PrimaryIdParam ? (
                        <Field
                          label="Customer"
                          name="CustomerIDEncrypted"
                          placeholder={"Select Customer"}
                          loadOptionsFn={loadOptionsCutomer}
                          component={CustomSelectAsync}
                          title={CustomerName}
                          labelFieldName="CustomerName"
                          required
                          onChange={handleChangeCustomer}
                        />
                      ) : (
                        <Field
                          label="Customer"
                          name="CustomerName"
                          component={CustomInput}
                          required
                          disabled={true}
                        />
                      )}
                    </Col>
                    {parseFloat(CustomerUnsettledAmount) > 0 && (
                      <Col lg={3}>
                        <div className="mb-2">
                          <lable className="mb-1">&nbsp;</lable>
                          <div>
                            <input
                              type="checkbox"
                              className="form-check-input me-2"
                              id={`chkIsSettledAgainstAdvance`}
                              name={`chkIsSettledAgainstAdvance`}
                              checked={IsSettledAgainstAdvance}
                              onChange={(e) => {
                                handleChangeIsSettledAgainstAdvance(
                                  e?.target?.checked
                                );
                              }}
                            />
                            <label className="mb-1 mt-1">
                              Settle Against Advance
                              {/* Settled Against Advance */}
                            </label>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                  {IsSettledAgainstAdvance == true && (
                    <>
                      <hr className="mt-1 mb-1"></hr>
                      <Row>
                        <label className="text-primary">Advance Receipt</label>
                        <Col lg={12}>
                          <div className="table-responsive active-projects style-1 shorting mb-2">
                            <div
                              id="advanceReceipt-tbl_wrapper"
                              className="dataTables_wrapper table-responsive no-footer"
                            >
                              <table
                                id="advanceReceipt-tbl"
                                className="table table-bordered no-footer mb-0"
                                style={{ width: "75%" }}
                              >
                                <thead>
                                  <tr>
                                    <th width="2%"></th>
                                    <th width="8%">Receipt No</th>
                                    <th width="10%">Receipt Date</th>
                                    <th width="20%">Contact Person</th>
                                    <th
                                      width="10%"
                                      className="pe-3"
                                      style={{ textAlign: "right" }}
                                    >
                                      Advance Amount
                                    </th>
                                    <th width="10%">Mode</th>
                                    <th
                                      width="10%"
                                      className="pe-3"
                                      style={{ textAlign: "right" }}
                                    >
                                      Settled Amount
                                    </th>
                                    <th width="10%" className="pe-3">
                                      Balance Amount
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ListOfAdvanceReceipt?.length > 0 ? (
                                    <>
                                      {ListOfAdvanceReceipt?.map(
                                        (item, index) => (
                                          <tr>
                                            <td style={{ minWidth: "30px" }}>
                                              <input
                                                type="checkbox"
                                                className="form-check-input m-0"
                                                id={`chkAdv${index}`}
                                                name={`chkAdv${index}`}
                                                checked={item.IsSelected}
                                                onChange={(e) => {
                                                  handleChangeIsSelectedAdvance(
                                                    e?.target?.checked,
                                                    index
                                                  );
                                                }}
                                              />
                                            </td>
                                            <td>{item.ReceiptNo}</td>
                                            <td>{item.ReceiptDate}</td>
                                            <td>{item.ContactPerson}</td>
                                            <td
                                              className="pe-3"
                                              style={{ textAlign: "right" }}
                                            >
                                              {item.AmountReceived}
                                            </td>
                                            <td>{item.PaymentMode}</td>
                                            <td
                                              className="pe-3"
                                              style={{ textAlign: "right" }}
                                            >
                                              {item.SettledAmount}
                                            </td>
                                            <td className="pe-3">
                                              {item.AvailableAmount}
                                            </td>
                                          </tr>
                                        )
                                      )}
                                      <tr>
                                        <td
                                          colSpan={7}
                                          className="p-1 pe-3"
                                          style={{
                                            textAlign: "right",
                                          }}
                                        >
                                          <label className="mb-0">
                                            Total Advance to be settled
                                          </label>
                                        </td>
                                        <td className="p-1 pe-3">
                                          <label className="mb-0">
                                            {TotalAdvanceToBeSettled}
                                          </label>
                                        </td>
                                      </tr>
                                    </>
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan={8}
                                        style={{ textAlign: "left" }}
                                      >
                                        <div>{"No Record Found..."}</div>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <ErrorMessage
                            name="ListOfAdvanceReceipt"
                            component="div"
                            className="invalid-feedback animated fadeInUp"
                            style={{ display: "block" }}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                  <hr className="mt-1 mb-1"></hr>
                  <Row>
                    <label className="text-primary">Invoice</label>
                    <Col lg={12}>
                      <div className="table-responsive active-projects style-1 shorting mb-2">
                        <div
                          id="invoice-tbl_wrapper"
                          className="dataTables_wrapper table-responsive no-footer"
                        >
                          <table
                            id="invoice-tbl"
                            className="table table-bordered no-footer mb-0"
                          >
                            <thead>
                              <tr>
                                <th
                                  width="2%"
                                  style={{ minWidth: "35px" }}
                                ></th>
                                <th width="6%" style={{ minWidth: "150px" }}>
                                  Invoice No
                                </th>
                                <th width="6%" style={{ minWidth: "120px" }}>
                                  Invoice Date
                                </th>
                                <th width="9%" style={{ minWidth: "250px" }}>
                                  Amount
                                </th>
                                <th width="9%" style={{ minWidth: "250px" }}>
                                  Received / Pending Amount
                                </th>
                                <th width="5%" style={{ minWidth: "80px" }}>
                                  TDS(%)
                                </th>
                                <th width="5%" style={{ minWidth: "150px" }}>
                                  TDS Calculation
                                </th>
                                <th width="8%" style={{ minWidth: "150px" }}>
                                  TDS Amount
                                </th>
                                <th width="5%" style={{ minWidth: "100px" }}>
                                  Kasar
                                </th>
                                <th width="5%" style={{ minWidth: "100px" }}>
                                  Bank Charges
                                </th>
                                <th
                                  width="8%"
                                  style={{
                                    minWidth: "150px",
                                    textAlign: "left",
                                  }}
                                >
                                  Receivable Amount
                                </th>
                                {IsSettledAgainstAdvance == false && (
                                  <>
                                    <th
                                      width="8%"
                                      style={{ minWidth: "200px" }}
                                    >
                                      Mode of Payment
                                    </th>
                                    <th
                                      width="8%"
                                      style={{ minWidth: "200px" }}
                                    >
                                      Receivable Bank
                                    </th>
                                    <th
                                      width="8%"
                                      style={{ minWidth: "250px" }}
                                    >
                                      Payment Reference No
                                    </th>
                                    <th
                                      width="8%"
                                      style={{
                                        minWidth: "250px",
                                        textAlign: "left",
                                      }}
                                    >
                                      Customer Bank Name
                                    </th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {ListOfInvoice?.length > 0 ? (
                                <>
                                  {ListOfInvoice?.map((item, index) => (
                                    <tr>
                                      <td style={{ verticalAlign: "top" }}>
                                        <input
                                          type="checkbox"
                                          className="form-check-input m-0"
                                          id={`chkInv${index}`}
                                          name={`chkInv${index}`}
                                          checked={item.IsSelected}
                                          onChange={(e) => {
                                            handleChangeIsSelectedInvoice(
                                              e?.target?.checked,
                                              index
                                            );
                                            calculateInvoiceTDSAmount(
                                              index,
                                              item.TDSPercentage,
                                              item.TDSCalcType
                                            );
                                          }}
                                        />
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        {item.InvoiceNo}{" "}
                                        <Link
                                          to="#"
                                          onClick={() => {
                                            setInvoiceDetails(
                                              ListOfInvoice?.filter(
                                                (x) =>
                                                  x.InvoiceIDEncrypted ==
                                                  item.InvoiceIDEncrypted
                                              )[0]
                                            );
                                            setShowInvoiceDetailModal(true);
                                          }}
                                        >
                                          {SVGICON.Reports}
                                        </Link>
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        {item.InvoiceDate}
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        Gross Amount: &nbsp;
                                        {item.GrossAmount}
                                        <br></br>
                                        Other Expense: &nbsp;
                                        {item.AdditionalAmount}
                                        <br></br>
                                        {parseFloat(item.DiscountAmount) > 0 ? (
                                          <>
                                            Discount: &nbsp;
                                            {item.DiscountAmount}
                                            <br></br>
                                          </>
                                        ) : (
                                          ""
                                        )}
                                        {item.GSTType == 2 ? (
                                          <>
                                            IGST: &nbsp;{item.IGSTAmount}
                                            <br></br>
                                          </>
                                        ) : item.GSTType == 3 ? (
                                          <>
                                            UGST: &nbsp;{item.UGSTAmount}
                                            <br></br>CGST: &nbsp;
                                            {item.CGSTAmount}
                                            <br></br>
                                          </>
                                        ) : (
                                          <>
                                            SGST: &nbsp;{item.SGSTAmount}
                                            <br></br>CGST: &nbsp;
                                            {item.CGSTAmount}
                                            <br></br>
                                          </>
                                        )}
                                        Net Amount: &nbsp;
                                        {item.NetAmount}
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        Received Amount: &nbsp;
                                        {item.ReceivedAmount}
                                        <br></br>
                                        Pending Amount: &nbsp;
                                        {item.PendingAmount}
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <Field
                                          type="number"
                                          name={`ListOfInvoice[${index}].TDSPercentage`}
                                          component={CustomInput}
                                          noMarginBottom={true}
                                          style={{ textAlign: "right" }}
                                          onChange={(e) => {
                                            if (e?.target?.value <= 100) {
                                              if (item.IsSelected == false) {
                                                handleChangeIsSelectedInvoice(
                                                  true,
                                                  index
                                                );
                                              }
                                              formik.setFieldValue(
                                                `ListOfInvoice[${index}].TDSPercentage`,
                                                e?.target?.value ?? ""
                                              );
                                              setTimeout(() => {
                                                calculateInvoiceTDSAmount(
                                                  index,
                                                  e?.target?.value ?? "",
                                                  item.TDSCalcType
                                                );
                                              }, 300);
                                            }
                                          }}
                                        />
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <Field
                                          name={`ListOfInvoice[${index}].TDSCalcType`}
                                          component={CustomSelect}
                                          options={tdsCalcTypeOptions}
                                          noMarginBottom={true}
                                          menuPosition="fixed"
                                          onChange={(e) => {
                                            if (item.IsSelected == false) {
                                              handleChangeIsSelectedInvoice(
                                                true,
                                                index
                                              );
                                            }
                                            formik.setFieldValue(
                                              `ListOfInvoice[${index}].TDSCalcType`,
                                              e?.value ?? ""
                                            );
                                            setTimeout(() => {
                                              calculateInvoiceTDSAmount(
                                                index,
                                                item.TDSPercentage,
                                                e?.value ?? ""
                                              );
                                            }, 300);
                                          }}
                                        />
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <Field
                                          type="number"
                                          name={`ListOfInvoice[${index}].TDSAmount`}
                                          component={CustomInput}
                                          noMarginBottom={true}
                                          style={{ textAlign: "right" }}
                                          onChange={(e) => {
                                            if (item.IsSelected == false) {
                                              handleChangeIsSelectedInvoice(
                                                true,
                                                index
                                              );
                                            }
                                            formik.setFieldValue(
                                              `ListOfInvoice[${index}].TDSAmount`,
                                              e?.target?.value ?? ""
                                            );
                                            setTimeout(() => {
                                              calculateReceivableAmount(
                                                index,
                                                item.PendingAmount,
                                                e?.target?.value ?? "",
                                                item.Kasar,
                                                item.BankCharges
                                              );
                                            }, 300);
                                          }}
                                        />
                                        <br></br>
                                        <Field
                                          name={`ListOfInvoice[${index}].FinancialYear`}
                                          component={CustomSelect}
                                          options={financialYearOptions}
                                          noMarginBottom={true}
                                          menuPosition="fixed"
                                          onChange={(e) => {
                                            handleChangeFinancialYear(e, index);
                                          }}
                                        />
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <Field
                                          type="number"
                                          name={`ListOfInvoice[${index}].Kasar`}
                                          component={CustomInput}
                                          noMarginBottom={true}
                                          style={{ textAlign: "right" }}
                                          onChange={(e) => {
                                            if (item.IsSelected == false) {
                                              handleChangeIsSelectedInvoice(
                                                true,
                                                index
                                              );
                                            }
                                            formik.setFieldValue(
                                              `ListOfInvoice[${index}].Kasar`,
                                              e?.target?.value ?? ""
                                            );
                                            setTimeout(() => {
                                              calculateReceivableAmount(
                                                index,
                                                item.PendingAmount,
                                                item.TDSAmount,
                                                e?.target?.value ?? "",
                                                item.BankCharges
                                              );
                                            }, 300);
                                          }}
                                        />
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <Field
                                          type="number"
                                          name={`ListOfInvoice[${index}].BankCharges`}
                                          component={CustomInput}
                                          noMarginBottom={true}
                                          style={{ textAlign: "right" }}
                                          onChange={(e) => {
                                            if (item.IsSelected == false) {
                                              handleChangeIsSelectedInvoice(
                                                true,
                                                index
                                              );
                                            }
                                            formik.setFieldValue(
                                              `ListOfInvoice[${index}].BankCharges`,
                                              e?.target?.value ?? ""
                                            );
                                            setTimeout(() => {
                                              calculateReceivableAmount(
                                                index,
                                                item.PendingAmount,
                                                item.TDSAmount,
                                                item.Kasar,
                                                e?.target?.value ?? ""
                                              );
                                            }, 300);
                                          }}
                                        />
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <Field
                                          type="number"
                                          name={`ListOfInvoice[${index}].Amount`}
                                          component={CustomInput}
                                          noMarginBottom={true}
                                          style={{ textAlign: "right" }}
                                          onChange={(e) => {
                                            if (item.IsSelected == false) {
                                              if (
                                                (e?.target?.value ?? 0) > 0 ||
                                                (item?.TDSAmount ?? 0) > 0 ||
                                                (item?.Kasar ?? 0) > 0 ||
                                                (item?.BankCharges ?? 0) > 0
                                              ) {
                                                handleChangeIsSelectedInvoice(
                                                  true,
                                                  index
                                                );
                                              }
                                            } else {
                                              if (
                                                (e?.target?.value ?? 0) == 0 &&
                                                (item?.TDSAmount ?? 0) == 0 &&
                                                (item?.Kasar ?? 0) == 0 &&
                                                (item?.BankCharges ?? 0) == 0
                                              ) {
                                                handleChangeIsSelectedInvoice(
                                                  false,
                                                  index
                                                );
                                              }
                                            }
                                            formik.setFieldValue(
                                              `ListOfInvoice[${index}].Amount`,
                                              e?.target?.value ?? ""
                                            );
                                            setTimeout(() => {
                                              setCalcInvoiceTotal(true);
                                            }, 300);
                                          }}
                                        />
                                      </td>
                                      {IsSettledAgainstAdvance == false && (
                                        <>
                                          <td style={{ verticalAlign: "top" }}>
                                            <Field
                                              name={`ListOfInvoice[${index}].ModeOfPayment`}
                                              component={CustomSelect}
                                              options={modeOfPaymentOptions}
                                              noMarginBottom={true}
                                              menuPosition="fixed"
                                            />
                                          </td>
                                          <td style={{ verticalAlign: "top" }}>
                                            <Field
                                              name={`ListOfInvoice[${index}].ReceivableBankIDEncrypted`}
                                              component={CustomSelect}
                                              options={receivableBankOptions}
                                              noMarginBottom={true}
                                              menuPosition="fixed"
                                            />
                                          </td>
                                          <td style={{ verticalAlign: "top" }}>
                                            <Field
                                              name={`ListOfInvoice[${index}].PaymentRefNo`}
                                              component={CustomInput}
                                              maxLength={200}
                                              noMarginBottom={true}
                                            />
                                          </td>
                                          <td style={{ verticalAlign: "top" }}>
                                            <Field
                                              name={`ListOfInvoice[${index}].PaymentRefBank`}
                                              component={CustomInput}
                                              maxLength={200}
                                              noMarginBottom={true}
                                            />
                                          </td>
                                        </>
                                      )}
                                    </tr>
                                  ))}
                                  <tr>
                                    <td
                                      colSpan={7}
                                      className="p-1 pe-3"
                                      style={{
                                        textAlign: "right",
                                      }}
                                    >
                                      <label className="mb-0">Total</label>
                                    </td>
                                    <td
                                      className="p-1 pe-3"
                                      style={{
                                        textAlign: "right",
                                      }}
                                    >
                                      <label className="mb-0">
                                        {TotalTDSAmount}
                                      </label>
                                    </td>
                                    <td
                                      className="p-1 pe-3"
                                      style={{
                                        textAlign: "right",
                                      }}
                                    >
                                      <label className="mb-0">
                                        {TotalKasar}
                                      </label>
                                    </td>
                                    <td
                                      className="p-1 pe-3"
                                      style={{
                                        textAlign: "right",
                                      }}
                                    >
                                      <label className="mb-0">
                                        {TotalBankCharges}
                                      </label>
                                    </td>
                                    <td
                                      className="p-1 pe-3"
                                      style={{
                                        textAlign: "right",
                                      }}
                                    >
                                      <label className="mb-0">
                                        {TotalReceivableAmount}
                                      </label>
                                    </td>
                                    {IsSettledAgainstAdvance == false && (
                                      <td colSpan={4}></td>
                                    )}
                                  </tr>
                                </>
                              ) : (
                                <tr>
                                  <td
                                    colSpan={15}
                                    style={{ textAlign: "left" }}
                                  >
                                    <div>{"No Record Found..."}</div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <ErrorMessage
                        name="ListOfInvoice"
                        component="div"
                        className="invalid-feedback animated fadeInUp"
                        style={{ display: "block" }}
                      />
                    </Col>
                  </Row>
                  <hr className="mt-1 mb-1"></hr>
                  <Row>
                    <Col lg={9}>
                      <Field
                        label="Remarks"
                        name="Remarks"
                        placeholder={"Enter Remarks"}
                        component={CustomTextArea}
                        maxLength={500}
                      />
                    </Col>
                  </Row>
                  <div className="stickyBottomButtons">
                    <hr className="mt-1 mb-2"></hr>
                    {IsCancelled == false && (
                      <button
                        type="submit"
                        className="btn btn-primary btn-sm me-2"
                      >
                        <i className="fa-solid fa-floppy-disk"></i> Submit
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={(e) => {
                        navigate(-1);
                      }}
                    >
                      <i className="fa fa-cancel"></i> Cancel
                    </button>
                  </div>
                </Form>
              </FormikProvider>
            </div>
          </Spin>
          <Modal
            className="fade"
            show={showInvoiceDetailModal}
            onHide={setShowInvoiceDetailModal}
            size="xl"
            dialogClassName="fullWidthModal"
          >
            <Modal.Header>
              <Modal.Title>Invoice Detail</Modal.Title>
              <Button
                type="button"
                variant=""
                className="btn-close"
                onClick={() => setShowInvoiceDetailModal(false)}
              ></Button>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col lg={12}>
                  <div className="table-responsive active-projects style-1 shorting mb-2">
                    <div
                      id="invoiceDetail-tbl_wrapper"
                      className="dataTables_wrapper table-responsive no-footer"
                    >
                      <table
                        id="invoiceDetail-tbl"
                        className="table table-bordered no-footer mb-0"
                      >
                        <thead>
                          <tr>
                            <th width="8%" style={{ minWidth: "140px" }}>
                              Invoice No
                            </th>
                            <th width="8%" style={{ minWidth: "120px" }}>
                              Invoice Date
                            </th>
                            <th width="18%" style={{ minWidth: "300px" }}>
                              Inward No
                            </th>
                            <th width="18%" style={{ minWidth: "300px" }}>
                              TC No
                            </th>
                            <th width="8%" style={{ minWidth: "120px" }}>
                              Gross Amount
                            </th>
                            <th width="8%" style={{ minWidth: "120px" }}>
                              Other Expense
                            </th>
                            <th width="6%" style={{ minWidth: "100px" }}>
                              Discount
                            </th>
                            <th
                              width="6%"
                              style={{
                                minWidth: "100px",
                                display:
                                  invoiceDetails.GSTType == 1 ? "" : "none",
                              }}
                            >
                              SGST
                            </th>
                            <th
                              width="6%"
                              style={{
                                minWidth: "100px",
                                display:
                                  invoiceDetails.GSTType == 2 ? "" : "none",
                              }}
                            >
                              IGST
                            </th>
                            <th
                              width="6%"
                              style={{
                                minWidth: "100px",
                                display:
                                  invoiceDetails.GSTType == 3 ? "" : "none",
                              }}
                            >
                              UGST
                            </th>
                            <th
                              width="6%"
                              style={{
                                minWidth: "100px",
                                display:
                                  invoiceDetails.GSTType != 2 ? "" : "none",
                              }}
                            >
                              CGST
                            </th>
                            <th
                              width="8%"
                              style={{
                                minWidth: "120px",
                                textAlign: "left",
                              }}
                            >
                              Net Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{invoiceDetails.InvoiceNo}</td>
                            <td>{invoiceDetails.InvoiceDate}</td>
                            <td style={{ whiteSpace: "normal" }}>
                              {invoiceDetails.InwardNo}
                            </td>
                            <td style={{ whiteSpace: "normal" }}>
                              {invoiceDetails.TCNo}
                            </td>
                            <td>{invoiceDetails.GrossAmount}</td>
                            <td>{invoiceDetails.AdditionalAmount}</td>
                            <td>{invoiceDetails.DiscountAmount}</td>
                            <td
                              style={{
                                display:
                                  invoiceDetails.GSTType == 1 ? "" : "none",
                              }}
                            >
                              {invoiceDetails.SGSTAmount}
                            </td>
                            <td
                              style={{
                                display:
                                  invoiceDetails.GSTType == 2 ? "" : "none",
                              }}
                            >
                              {invoiceDetails.IGSTAmount}
                            </td>
                            <td
                              style={{
                                display:
                                  invoiceDetails.GSTType == 3 ? "" : "none",
                              }}
                            >
                              {invoiceDetails.UGSTAmount}
                            </td>
                            <td
                              style={{
                                display:
                                  invoiceDetails.GSTType != 2 ? "" : "none",
                              }}
                            >
                              {invoiceDetails.CGSTAmount}
                            </td>
                            <td
                              style={{
                                textAlign: "left",
                              }}
                            >
                              {invoiceDetails.NetAmount}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary btn-sm"
                onClick={() => setShowInvoiceDetailModal(false)}
              >
                OK
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default AddPaymentReceipt;
